/**
 * A postal address held as its parts rather than one blob of text.
 *
 * The parts exist because the whole is not the only thing anyone wants: an
 * invoice prints the lines in order, an export wants one column per part, and
 * a country is a country whether it was typed into line 3 or not. Keeping them
 * apart on the way in is the only moment they can be told apart cheaply — a
 * stored blob cannot be split back into them with any confidence.
 *
 * The country is not part of this shape. The records that carry an address
 * already carry a `country` of their own, and a second one would be a second
 * answer to the same question; the caller passes its own column in as
 * {@link formatPostalAddress}'s `country`.
 */
export interface PostalAddress {
  address_line1: string | null;
  address_line2: string | null;
  address_city: string | null;
  address_state: string | null;
  address_postal_code: string | null;
}

/** The address columns, in the order a form and an export should show them. */
export const POSTAL_ADDRESS_COLUMNS = [
  "address_line1",
  "address_line2",
  "address_city",
  "address_state",
  "address_postal_code",
] as const satisfies readonly (keyof PostalAddress)[];

const trim = (value: string | null | undefined): string => (value ?? "").trim();

/**
 * The address as the lines you would write on an envelope, blanks dropped.
 *
 * City, state and postal code share a line — "Miami, Florida 33131" — because
 * that is one place, and three lines holding one word each read as three
 * places. Everything else gets its own line, and a part that was never filled
 * in takes no line at all, so a two-field address prints as two lines rather
 * than two lines and four gaps.
 *
 * Returns an empty array when nothing is set, which the callers use to decide
 * whether there is an address to show at all.
 */
export function formatPostalAddress(
  address: Partial<PostalAddress>,
  country?: string | null,
): string[] {
  const locality = [
    [trim(address.address_city), trim(address.address_state)].filter(Boolean).join(", "),
    trim(address.address_postal_code),
  ]
    .filter(Boolean)
    .join(" ");

  return [trim(address.address_line1), trim(address.address_line2), locality, trim(country)].filter(
    Boolean,
  );
}

/** The same address on one line, for a cell or a column that has only one. */
export function formatPostalAddressInline(
  address: Partial<PostalAddress>,
  country?: string | null,
): string {
  return formatPostalAddress(address, country).join(", ");
}

/**
 * A company's registered office, part by part.
 *
 * Not a {@link PostalAddress}: an office in the BVI has no state and rarely a
 * postcode anyone uses, and the territory has towns rather than cities, so it
 * takes three free lines, a town or city, and a country. The country is its own
 * column here rather than borrowed from `jurisdiction`, because a company
 * incorporated in one place can keep its office in another.
 *
 * `registered_office` on the company is the printed form of these parts, kept
 * by the API on every write so the invoice and everything else that reads the
 * address as lines goes on reading it unchanged.
 */
export interface RegisteredOffice {
  registered_office_line1: string | null;
  registered_office_line2: string | null;
  registered_office_line3: string | null;
  registered_office_city: string | null;
  registered_office_country: string | null;
}

/** The office columns, in the order a form and a sheet should show them. */
export const REGISTERED_OFFICE_COLUMNS = [
  "registered_office_line1",
  "registered_office_line2",
  "registered_office_line3",
  "registered_office_city",
  "registered_office_country",
] as const satisfies readonly (keyof RegisteredOffice)[];

/** The office as envelope lines, one per part, blanks dropped. */
export function formatRegisteredOffice(office: Partial<RegisteredOffice>): string[] {
  return REGISTERED_OFFICE_COLUMNS.map((column) => trim(office[column])).filter(Boolean);
}

/**
 * The three address lines out of an office typed as one block, before it had
 * parts.
 *
 * Only a starting point for a person to correct: there is no telling a town
 * from a street in free text, so everything goes into the lines and the town
 * and country are left for whoever opens the company next. A block of more
 * than three lines keeps the rest on line 3 rather than losing it.
 */
export function registeredOfficeFromText(text: string | null | undefined): RegisteredOffice {
  const lines = (text ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  return {
    registered_office_line1: lines[0] ?? null,
    registered_office_line2: lines[1] ?? null,
    registered_office_line3: lines.slice(2).join(", ") || null,
    registered_office_city: null,
    registered_office_country: null,
  };
}
